import React, { createElement, FC, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import GetStartedPage from './pages/get-started/GetStartedPage';
import SelectThemePage from './pages/select-theme/SelectThemePage';
import './shared/services/middlewares/axios';
import SiteDesignPage from './pages/site-design/SiteDesignPage';
import { createGlobalStyle } from 'styled-components';
import { fontList, fontsForWeb } from '@mnz/builder';
import config from './config/config';
import RestaurantsPage from './pages/select-restaurant/RestaurantsPage';
import HomePage from './pages/home/HomePage';
import PublishSuccessPage from './pages/publish-success/PublishSuccessPage';
import { useQuery } from './hooks/use-query';
import PublicRoute from './components/common/router/PublicRoute';
import OnBoardingPage from './pages/OnBoardingPage';
import ClaimAccountPage from './pages/on-boarding/ClaimAccountPage';
import ProtectedRoute from './components/common/router/ProtectedRoute';
import { useGetCurrentUserLazyQuery } from './generated/types';
import { useIntercom } from 'react-use-intercom';
import { useAsync } from 'react-use';
import { isLoggedIn } from './shared/libs/auth/auth';
import * as FullStory from '@fullstory/browser';
import { logEvent, setUserId } from './common/firebase/firebase';

export const GlobalStyle = createGlobalStyle(
    () => `
    ${Object.keys(fontList)
        .map((f) => fontsForWeb(config.fontPath, f))
        .join('')}
    body{
        background-color:#F8F8F9
    }
`,
);

const App: FC = () => {
    const [viewPortWidth, setViewPortWidth] = useState<number>();
    const [getCurrentUser] = useGetCurrentUserLazyQuery();
    const { boot } = useIntercom();
    const location = useLocation();
    const isFullWidth =
        location.pathname.includes('/select-theme') ||
        location.pathname.includes('/home') ||
        location.pathname.includes('/getstarted/template');

    useEffect(() => {
        const windowHeight = () => {
            setViewPortWidth(window.innerHeight);
        };
        window.addEventListener('resize', windowHeight);
        windowHeight();
        return () => {
            window.removeEventListener('resize', windowHeight);
        };
    }, []);

    useEffect(() => {
        const disableGestureStart = (e: Event) => {
            e.preventDefault();
        };
        document.addEventListener('gesturestart', disableGestureStart);
        return () => {
            document.removeEventListener('gesturestart', disableGestureStart);
        };
    });

    useEffect(() => {
        logEvent('page_view', {
            page_path: location.pathname + location.search,
        });
    }, [location]);

    useAsync(async () => {
        if (isLoggedIn()) {
            const res = await getCurrentUser();
            const user = res.data?.currentUser;
            boot({
                userId: user?.userId || undefined,
                name: user?.name || undefined,
                email: user?.email || undefined,
            });
            if (user) {
                setUserId(user.userId);

                FullStory.setUserVars({
                    uid: user.userId,
                    displayName: user.name || '',
                    email: user.email || '',
                });
            }
        }
    }, []);
    return (
        <div
            className={`h-screen flex flex-col mx-auto`}
            style={{
                maxWidth: !isFullWidth ? 750 : 'none',
                maxHeight: viewPortWidth ? `${viewPortWidth}px` : 'auto',
            }}
        >
            <GlobalStyle />
            <Switch>
                <Route path="/" exact render={() => <GetStartedPage />} />
                <Route path="/restaurants" exact render={() => <Redirect to="business"></Redirect>} />
                <Route path="/business" exact render={() => <RestaurantsPage />} />
                <Route
                    path="/account/:accountId/select-theme"
                    exact
                    render={() => (
                        <ProtectedRoute>
                            <SelectThemePage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="/account/:accountId/publish-success"
                    exact
                    render={() => (
                        <ProtectedRoute>
                            <PublishSuccessPage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="/account/:accountId"
                    exact
                    render={({ match }) => {
                        return createElement(() => {
                            const query = useQuery();
                            return (
                                <Redirect
                                    to={{
                                        pathname: `/account/${match.params.accountId}/home`,
                                        search: `?${query.toString()}`,
                                    }}
                                />
                            );
                        });
                    }}
                />
                <Route
                    path="/account/:accountId/home"
                    exact
                    render={() => (
                        <ProtectedRoute>
                            <HomePage />
                        </ProtectedRoute>
                    )}
                />
                <Route
                    path="/account/:accountId/site-design"
                    render={() => (
                        <ProtectedRoute>
                            <SiteDesignPage />
                        </ProtectedRoute>
                    )}
                    exact
                />

                <Route path="/getstarted">
                    <ProtectedRoute useSignUp>
                        <OnBoardingPage />
                    </ProtectedRoute>
                </Route>
                <Route
                    path="/claim/:accountId"
                    exact
                    render={() => (
                        <PublicRoute>
                            <ClaimAccountPage />
                        </PublicRoute>
                    )}
                />
            </Switch>
        </div>
    );
};

export default App;
